import { defineComponent, ref, reactive, computed, onBeforeMount, type Ref } from 'vue'
import HomeBannerForm from '@/modules/home/components/HomeBannerForm/HomeBannerForm.vue'
import HomeInfoBlocks from '@/modules/home/components/HomeInfoBlocks/HomeInfoBlocks.vue'
import { useRouter } from 'vue-router'
import { useBannersStore } from '@/store/banners'
import { useCatalogStore } from '@/store/catalog'
import { useApiCall } from '@/composables/useApiCall'
import { useScroll } from '@/composables/useScroll'
import { getMainPageDataApiCall, updateMainPageDataApiCal } from '@/api/mainPage'
import {
  HOME_ELEMENTS_TABLE_HEADINGS,
  HOME_ELEMENTS_TITLES,
  HOME_AD_BANNERS_TABLE_HEADINGS,
  HOME_AD_BANNERS_TITLES,
} from '@/constants/headings'
import type { IBanner } from '@/types/banners'
import type { DefaultError } from '@/types/httpError'
import type {
  IMainPageData,
  UpdateMainPageDataPayload,
  GetMainPageDataResponse,
} from '@/types/mainPage'
import { useAppStore } from '@/store/app/app'
import { useServicesStore } from '@/store/services'

export default defineComponent({
  name: 'HomeView',
  components: {
    HomeBannerForm,
    HomeInfoBlocks,
  },
  setup() {
    const bannersStore = useBannersStore()
    const catalogStore = useCatalogStore()
    const servicesStore = useServicesStore()

    const banners = computed(() => bannersStore.banners ?? [])
    const elemsFields = ref<Omit<IMainPageData, 'id'>>({
      sliderL: true,
      sliderS: false,
      loyalty: true,
      myBookings: true,
      myOrders: true,
    })
    const limitedFields = computed(() => elemsFields.value)
    const selectedBanner = ref<Partial<IBanner> | null>(null)
    const openedPopup = reactive({
      confirm: false,
      banner: false,
    })
    const confirmBannerError = ref('')

    const router = useRouter()
    const currentRoute = computed(() => router.currentRoute.value)

    const scroll = useScroll()

    const {
      isLoading: isMainPageDataLoading,
      data: mainPageData,
      executeApiCall: getMainPageData,
      error: getMainPageError,
    } = useApiCall<GetMainPageDataResponse, DefaultError>(getMainPageDataApiCall, true)

    const {
      isLoading: isUpdateMainPageLoading,
      executeApiCall: updateMainPageData,
      error: updateMainPageError,
    } = useApiCall<GetMainPageDataResponse, DefaultError, UpdateMainPageDataPayload>(
      updateMainPageDataApiCal,
      true,
    )

    const onUpdateElemsValue = async (key: keyof Omit<IMainPageData, 'id'>, val: boolean) => {
      if (key === 'sliderL') {
        elemsFields.value.sliderL = val
        elemsFields.value.sliderS = !val
      } else if (key === 'sliderS') {
        elemsFields.value.sliderS = val
        elemsFields.value.sliderL = !val
      } else {
        elemsFields.value[key] = val
      }

      try {
        await updateMainPageData(elemsFields.value)
      } catch {
        if (updateMainPageError.value) {
          console.error(updateMainPageError.value.data.error)
        }
      }
    }

    // const getBannerCategory = (id: number | null) =>
    //   catalogStore.parentCategories?.find(c => c.id === id)?.name ?? '-'

    const onUpdateBannersValue = async (id: number, val: boolean) => {
      await bannersStore.updateBanner({ id, data: { isActive: val } })
    }

    const openBannerForm = (id?: number) => {
      const bannerToEdit = banners.value.find(b => b.id === id)
      if (bannerToEdit) {
        selectedBanner.value = bannerToEdit
      }
      openedPopup.banner = true
    }

    const closeBannerForm = () => {
      if (selectedBanner.value) {
        selectedBanner.value = null
      }
      openedPopup.banner = false
    }

    const handleBannerRemove = async () => {
      if (selectedBanner.value?.id) {
        await bannersStore.deleteBanner({ id: selectedBanner.value.id })
      }

      if (bannersStore.error) {
        confirmBannerError.value = bannersStore.error.error
        return
      }
      closeConfirmPopup()
    }

    const removeBannerClick = (id: number) => {
      selectedBanner.value = {
        id,
      }
      openedPopup.confirm = true
    }

    const closeConfirmPopup = () => {
      openedPopup.confirm = false
      selectedBanner.value = null
    }
    const isLoading = ref(true)
    const errorInterval: Ref<null | ReturnType<typeof setInterval>> = ref(null)

    const saveData = () => {
      if (mainPageData.value) {
        const { id, ...rest } = mainPageData.value.data
        elemsFields.value = rest
        if (errorInterval.value) {
          clearInterval(errorInterval.value)
          errorInterval.value = null
        }
        isLoading.value = false
      }
    }
    const appStore = useAppStore()
    onBeforeMount(async () => {
      await appStore.getAppData()
      await catalogStore.getParentCategories()
      await servicesStore.getParentCategories()

      if (currentRoute.value.name !== 'home') {
        isLoading.value = false
        return
      }

      try {
        await getMainPageData()
        if (mainPageData.value && Object.keys(mainPageData.value.data).includes('loyalty')) {
          saveData()
        } else {
          errorInterval.value = setInterval(async () => {
            await getMainPageData()
            if (mainPageData.value && Object.keys(mainPageData.value.data).includes('loyalty')) {
              saveData()
            }
          }, 5000)
        }
      } catch {
        if (getMainPageError.value) {
          console.error(getMainPageError.value.data.error)
        }
      }
      await bannersStore.getBanners()

      const promoPosition = localStorage.getItem('promoPosition')

      if (promoPosition) {
        scroll.scrollTo(+promoPosition, 0)
        localStorage.removeItem('promoPosition')
      }
    })

    return {
      HOME_ELEMENTS_TABLE_HEADINGS,
      HOME_ELEMENTS_TITLES,
      HOME_AD_BANNERS_TABLE_HEADINGS,
      HOME_AD_BANNERS_TITLES,
      currentRoute,
      banners,
      // productCategories,
      // serviceCategories,
      elemsFields,
      limitedFields,
      selectedBanner,
      openedPopup,
      isMainPageDataLoading,
      isUpdateMainPageLoading,
      onUpdateElemsValue,
      // getBannerCategory,
      onUpdateBannersValue,
      openBannerForm,
      closeBannerForm,
      handleBannerRemove,
      confirmBannerError,
      removeBannerClick,
      closeConfirmPopup,
      isLoading,
      appStore,
    }
  },
})
